import { AggregationType, ChartType } from '@y42/json-schema';
import { assertTuple } from '@y42/util/assert-tuple';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import {
  addDays,
  addHours,
  addMinutes,
  addWeeks,
  differenceInCalendarISOWeeks,
  differenceInCalendarWeeks,
  format,
  isBefore,
  isSameWeek,
  parse,
  setDay,
  setISOWeek,
  setISOWeekYear,
  setYear,
  startOfISOWeek,
  startOfMonth,
  startOfQuarter,
  startOfYear,
} from 'date-fns';
import type { XAXisComponentOption } from 'echarts';
import { AxisTypeEnum } from '../../models/widget/chart-models/sub-models/datos-axis.model';
import { findUniqueValues } from '../../util';
import { VisualizationWidgetUIModel } from './visualization-widget.model';

export const InterpolatableAggregationTypes = [
  AggregationType.DayMonthYear,
  AggregationType.WeekMonth,
  AggregationType.WeekMonthYear,
  AggregationType.WeekNumberMonth,
  AggregationType.DayMonthYear,
  AggregationType.QuarterYear,
  AggregationType.MonthYear,
  AggregationType.WeekNumberMonthYear,
  AggregationType.WeekNumberIsoYear,
  AggregationType.DateHour,
  AggregationType.DateMinute,
  AggregationType.Year,
  AggregationType.Quarter,
  AggregationType.Month,
  AggregationType.Week,
  AggregationType.WeekNumber,
  AggregationType.Day,
  AggregationType.Hour,
] as const;

export type InterpolatableAggregationType = (typeof InterpolatableAggregationTypes)[number];

export const AggregationTypeToRecommendedXAxisType = {
  ...InterpolatableAggregationTypes.reduce(
    // we set AxisType.time for these aggregation types
    // but in the final EChartsOptions we override it with "category"
    // because since it's a string type ECharts cannot treat it differently
    (values, current) => ({ ...values, [current]: AxisTypeEnum.time }),
    {},
  ),
} as Record<InterpolatableAggregationType, XAXisComponentOption['type']>;

const leftPad = (val: number) => (val >= 10 ? `${val}` : `0${val}`);
const getExtrema = (extrema: string) => extrema.split('-').map((value) => Number.parseInt(value));
const getArrayOfLength = (length: number) => [...new Array(length).keys()];
export const InterpolatableAggregationTypeToInterpolationFn: Record<
  InterpolatableAggregationType,
  (min: string, max: string) => string[]
> = {
  DAY_MONTH_YEAR: (min, max) => {
    const dateFormat = 'yyyy-MM-dd';
    let minDate = parse(min, dateFormat, Date.now());
    const maxDate = parse(max, dateFormat, Date.now());
    const arr: Date[] = [minDate];
    while (isBefore(minDate, maxDate)) {
      minDate = addDays(minDate, 1);
      arr.push(minDate);
    }
    return arr.map((date) => format(date, dateFormat));
  },
  DATE_HOUR: (min, max) => {
    const dateFormat = 'yyyy-MM-dd HH';
    let minDate = parse(min, dateFormat, Date.now());
    const maxDate = parse(max, dateFormat, Date.now());
    const arr: Date[] = [minDate];
    while (isBefore(minDate, maxDate)) {
      minDate = addHours(minDate, 1);
      arr.push(minDate);
    }
    return arr.map((date) => format(date, dateFormat));
  },
  DATE_MINUTE: (min, max) => {
    const dateFormat = 'yyyy-MM-dd HH:mm';
    let minDate = parse(min, dateFormat, Date.now());
    const maxDate = parse(max, dateFormat, Date.now());
    const arr: Date[] = [minDate];
    while (isBefore(minDate, maxDate)) {
      minDate = addMinutes(minDate, 1);
      arr.push(minDate);
    }
    return arr.map((date) => format(date, dateFormat));
  },
  WEEK_MONTH: (min, max) => {
    const arr = [] as Array<{ month: number; weekday: number }>;
    // 02-3
    const [minMonth, minWeekday] = assertTuple(
      min.split('-').map((value) => Number.parseInt(value)),
      2,
    );
    // 11-6
    const [maxMonth, maxWeekday] = assertTuple(
      max.split('-').map((value) => Number.parseInt(value)),
      2,
    );

    let index = 0;
    for (let month = 1; month <= 12; month++) {
      for (let weekday = 1; weekday <= 7; weekday++) {
        arr[index++] = { month, weekday };
      }
    }

    return arr
      .filter(({ month, weekday }) => {
        if (month < minMonth || month > maxMonth) {
          return false;
        } else if (month === minMonth && weekday < minWeekday) {
          return false;
        } else if (month === maxMonth && weekday > maxWeekday) {
          return false;
        }

        return true;
      })
      .map(({ month, weekday }) => `${leftPad(month)}-${weekday}`);
  },
  WEEK_MONTH_YEAR: (min, max) => {
    const arr = [] as Array<{ year: number; month: number; weekday: number }>;
    const [minYear, minMonth, minWeekday] = assertTuple(
      min.split('-').map((value) => Number.parseInt(value)),
      3,
    );
    const [maxYear, maxMonth, maxWeekday] = assertTuple(
      max.split('-').map((value) => Number.parseInt(value)),
      3,
    );

    let index = 0;
    for (let year = minYear; year <= maxYear; year++) {
      for (let month = 1; month <= 12; month++) {
        for (let weekday = 1; weekday <= 7; weekday++) {
          arr[index++] = { year, month, weekday };
        }
      }
    }

    return arr
      .filter(({ year, month, weekday }) => {
        if (year < minYear || year > maxYear) {
          return false;
        } else if (month < minMonth || month > maxMonth) {
          return false;
        } else if (month === minMonth && weekday < minWeekday) {
          return false;
        } else if (month === maxMonth && weekday > maxWeekday) {
          return false;
        }

        return true;
      })
      .map(({ year, month, weekday }) => `${year}-${leftPad(month)}-${weekday}`);
  },
  WEEK_NUMBER_MONTH: (min, max) => {
    const arr: Date[] = [];
    const [, minWeekNumber] = assertTuple(
      min.split('-').map((v) => Number.parseInt(v)),
      2,
    );
    const [, maxWeekNumber] = assertTuple(
      max.split('-').map((v) => Number.parseInt(v)),
      2,
    );

    let minDate = setISOWeek(Date.now(), minWeekNumber);
    const maxDate = setISOWeek(Date.now(), maxWeekNumber);
    while (differenceInCalendarWeeks(minDate, maxDate) < 0 || isSameWeek(minDate, maxDate)) {
      arr.push(minDate);
      minDate = addWeeks(minDate, 1);
    }

    return findUniqueValues(arr.map((date) => format(date, 'MM-II')));
  },
  HOUR: (min, max) => {
    const [minHour] = assertTuple(getExtrema(min), 1);
    const [maxHour] = assertTuple(getExtrema(max), 1);
    return getArrayOfLength(maxHour - minHour + 1).map((hour) => `${leftPad(hour + minHour)}`);
  },
  DAY: (min, max) => {
    const [minDay] = assertTuple(getExtrema(min), 1);
    const [maxDay] = assertTuple(getExtrema(max), 1);
    return getArrayOfLength(maxDay - minDay + 1).map((day) => `${leftPad(day + minDay)}`);
  },
  WEEK: (min, max) => {
    const [minDay] = assertTuple(getExtrema(min), 1);
    const [maxDay] = assertTuple(getExtrema(max), 1);
    return getArrayOfLength(maxDay - minDay + 1).map((week) => `${week + minDay}`);
  },
  WEEK_NUMBER: (min, max) => {
    const [minWeeknumber] = assertTuple(getExtrema(min), 1);
    const [maxWeeknumber] = assertTuple(getExtrema(max), 1);
    return getArrayOfLength(maxWeeknumber - minWeeknumber + 1).map(
      (weeknumber) => `${leftPad(weeknumber + minWeeknumber)}`,
    );
  },
  MONTH: (min, max) => {
    const [minMonth] = assertTuple(getExtrema(min), 1);
    const [maxMonth] = assertTuple(getExtrema(max), 1);
    return getArrayOfLength(maxMonth - minMonth + 1).map((month) => `${leftPad(month + minMonth)}`);
  },
  QUARTER: (min, max) => {
    const [minQuarter] = assertTuple(getExtrema(min), 1);
    const [maxQuarter] = assertTuple(getExtrema(max), 1);
    return getArrayOfLength(maxQuarter - minQuarter + 1).map((quarter) => `${quarter + minQuarter}`);
  },
  YEAR: (min, max) => {
    const [minYear] = assertTuple(getExtrema(min), 1);
    const [maxYear] = assertTuple(getExtrema(max), 1);
    return getArrayOfLength(maxYear - minYear + 1).map((year) => `${year + minYear}`);
  },
  QUARTER_YEAR: (min, max) => {
    const arr = [] as Array<{ year: number; quarter: number }>;
    const [minYear, minQuarter] = assertTuple(getExtrema(min), 2);
    const [maxYear, maxQuarter] = assertTuple(getExtrema(max), 2);

    let index = 0;
    for (let year = minYear; year <= maxYear; year++) {
      for (let quarter = 1; quarter <= 4; quarter++) {
        arr[index++] = { year, quarter };
      }
    }

    return arr
      .filter(({ year, quarter }) => {
        if (year < minYear || year > maxYear) {
          return false;
        } else if (year === minYear && quarter < minQuarter) {
          return false;
        } else if (year === maxYear && quarter > maxQuarter) {
          return false;
        }

        return true;
      })
      .map(({ year, quarter }) => `${year}-${quarter}`);
  },
  MONTH_YEAR: (min, max) => {
    const arr = [] as Array<{ year: number; month: number }>;
    const [minYear, minMonth] = assertTuple(getExtrema(min), 2);
    const [maxYear, maxMonth] = assertTuple(getExtrema(max), 2);

    let index = 0;
    for (let year = minYear; year <= maxYear; year++) {
      for (let month = 1; month <= 12; month++) {
        arr[index++] = { year, month };
      }
    }

    return arr
      .filter(({ year, month }) => {
        if (year < minYear || year > maxYear) {
          return false;
        } else if (year === minYear && month < minMonth) {
          return false;
        } else if (year === maxYear && month > maxMonth) {
          return false;
        }

        return true;
      })
      .map(({ year, month }) => `${year}-${leftPad(month)}`);
  },
  WEEK_NUMBER_MONTH_YEAR: (min, max) => {
    const [minYear, , minWeekNumber] = assertTuple(getExtrema(min), 3);
    const [maxYear, , maxWeekNumber] = assertTuple(getExtrema(max), 3);

    let minDate = setISOWeek(setYear(Date.now(), minYear), minWeekNumber);
    const maxDate = setISOWeek(setYear(Date.now(), maxYear), maxWeekNumber);
    const arr: Date[] = [];
    while (differenceInCalendarWeeks(minDate, maxDate) < 0 || isSameWeek(minDate, maxDate)) {
      arr.push(minDate);
      minDate = addWeeks(minDate, 1);
    }

    return findUniqueValues(arr.map((date) => format(date, 'yyyy-MM-II')));
  },
  WEEK_NUMBER_ISO_YEAR: (min, max) => {
    const [minYear, minWeekNumber] = assertTuple(getExtrema(min), 2);
    const [maxYear, maxWeekNumber] = assertTuple(getExtrema(max), 2);

    let minDate = setISOWeek(setISOWeekYear(Date.now(), minYear), minWeekNumber);
    const maxDate = setISOWeek(setISOWeekYear(Date.now(), maxYear), maxWeekNumber);

    const arr: Date[] = [];
    while (differenceInCalendarISOWeeks(minDate, maxDate) < 0 || isSameWeek(minDate, maxDate)) {
      arr.push(minDate);
      minDate = addWeeks(minDate, 1);
    }

    return findUniqueValues(arr.map((date) => format(date, 'RRRR-II')));
  },
};

export const InterpolatableAggregationTypeToFormattingFn: Partial<
  Record<
    InterpolatableAggregationType,
    (state: VisualizationWidgetUIModel<ChartType.BarLineChart>) => (value: string) => string
  >
> = {
  [AggregationType.DayMonthYear]: (state) => {
    const { date_format } = state.settings.dimensionAxis.axisLabel;
    const defaultFormat = 'yyyy-MM-dd';
    return (value: string) => {
      const date = parse(value, defaultFormat, new Date());
      return format(date, date_format || defaultFormat);
    };
  },
  [AggregationType.DateHour]: (state) => {
    const { date_format } = state.settings.dimensionAxis.axisLabel;
    const defaultFormat = 'yyyy-MM-dd HH';
    return (value: string) => {
      const date = parse(value, defaultFormat, new Date());
      return format(date, date_format || defaultFormat);
    };
  },
  [AggregationType.DateMinute]: (state) => {
    const { date_format } = state.settings.dimensionAxis.axisLabel;
    const defaultFormat = 'yyyy-MM-dd HH:mm';
    return (value: string) => {
      const date = parse(value, defaultFormat, new Date());
      return format(date, date_format || defaultFormat);
    };
  },
  [AggregationType.WeekNumberMonth]: (state) => {
    const { date_format } = state.settings.dimensionAxis.axisLabel;
    return (value: string) => (date_format === 'ww-MM' ? value.split('-').reverse().join('-') : value);
  },
  [AggregationType.QuarterYear]: (state) => {
    const { date_format } = state.settings.dimensionAxis.axisLabel;
    const defaultFormat = 'yyyy-Q';
    return (value: string) => {
      let date = parse(value, defaultFormat, new Date());
      date = startOfQuarter(date);
      return format(date, date_format || defaultFormat);
    };
  },
  [AggregationType.MonthYear]: (state) => {
    const { date_format } = state.settings.dimensionAxis.axisLabel;
    const defaultFormat = 'yyyy-MM';
    return (value: string) => {
      let date = parse(value, defaultFormat, new Date());
      date = startOfMonth(date);
      return format(date, date_format || defaultFormat);
    };
  },
  [AggregationType.WeekMonth]: (state) => {
    const { date_format } = state.settings.dimensionAxis.axisLabel;
    return (value: string) => {
      const [month, weekday] = assertTuple(
        value.split('-').map((v) => Number.parseInt(v)),
        2,
      );
      const dayOfWeek = format(setDay(new Date(), weekday), 'EEEE');
      return (date_format || '').startsWith('EEEE')
        ? `${dayOfWeek}-${leftPad(month)}`
        : `${leftPad(month)}-${dayOfWeek}`;
    };
  },
  // deprecated
  [AggregationType.WeekNumberMonthYear]: (state) => {
    const { date_format } = state.settings.dimensionAxis.axisLabel;
    return (value: string) => {
      const [year, month, weeknumber] = value.split('-');
      const dateFormatToString: Record<string, string> = {
        'yyyy W': `${year} W${weeknumber}`,
        'ww-MM-yyyy': `${weeknumber}-${month}-${year}`,
        'MM-ww-yyyy': `${month}-${weeknumber}-${year}`,
        'yyyy-MM-ww': `${year}-${month}-${weeknumber}`,
      };
      return dateFormatToString[date_format] || value;
    };
  },
  [AggregationType.WeekNumberIsoYear]: (state) => {
    const { date_format } = state.settings.dimensionAxis.axisLabel;
    return (value: string) => {
      const [year, weeknumber] = assertTuple(
        value.split('-').map((v) => Number.parseInt(v)),
        2,
      );
      let date = setISOWeek(setISOWeekYear(new Date(), year), weeknumber);
      date = startOfISOWeek(date);
      const dateFormatToString: Record<string, string> = {
        'yyyy W': `${year} W${weeknumber}`,
        'ww-yyyy': `${weeknumber}-${year}`,
        'yyyy ww': `${year}-${weeknumber}`,
        'yyyy-MM-dd': format(date, 'yyyy-MM-dd'),
      };
      return dateFormatToString[date_format] || value;
    };
  },
  [AggregationType.WeekMonthYear]: (state) => {
    const { date_format } = state.settings.dimensionAxis.axisLabel;
    return (value: string) => {
      const [year, month, weekday] = assertTuple(value.split('-'), 3);
      const dayOfWeek = format(setDay(new Date(), Number.parseInt(weekday)), 'EEEE');
      const dateFormatToString: Record<string, string> = {
        'EEEE-MM-yyyy': `${dayOfWeek}-${month}-${year}`,
        'MM-EEEE-yyyy': `${month}-${dayOfWeek}-${year}`,
        'yyyy-MM-EEEE': `${year}-${month}-${dayOfWeek}`,
      };
      return dateFormatToString[date_format] || value;
    };
  },
  [AggregationType.Year]: (state) => {
    const { date_format } = state.settings.dimensionAxis.axisLabel;
    return (value: string) => {
      const year = Number.parseInt(value);
      let date = setYear(new Date(), year);
      date = startOfYear(date);
      return date_format ? format(date, date_format) : value;
    };
  },
};
